import React, { useState, useEffect } from "react";

const FilePreview = ({ fileUrl, onRemove, fileName }) => {
  const [previewType, setPreviewType] = useState('loading');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const handleClick = () => {
    if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
  };

  const getFileExtension = (url) => {
    if (!url) return '';
    const ext = url.split('.').pop().toLowerCase();
    return ext.split('?')[0].split('#')[0];
  };

  const getFileIcon = (ext) => {
    const iconMap = {
      pdf: 'bx-file-pdf',
      doc: 'bx-file-doc',
      docx: 'bx-file-doc',
      txt: 'bx-file-txt',
      xls: 'bx-file-xls',
      xlsx: 'bx-file-xls',
      ppt: 'bx-file-ppt',
      pptx: 'bx-file-ppt',
      zip: 'bx-file-archive',
      rar: 'bx-file-archive',
      mp4: 'bx-file-video',
      avi: 'bx-file-video',
      mov: 'bx-file-video',
      mp3: 'bx-file-audio',
      wav: 'bx-file-audio',
      default: 'bx-file'
    };
    return iconMap[ext] || iconMap.default;
  };

  useEffect(() => {
    if (!fileUrl) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const ext = getFileExtension(fileUrl);
    const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'tiff', 'tif', 'jfif', 'pjpeg', 'pjp'];
    
    setImageLoaded(false);
    
    if (imageExts.includes(ext)) {
      setPreviewType('image');
      setPreviewUrl(fileUrl);
    } else if (ext === 'pdf') {
      setPreviewType('pdf');
      setPreviewUrl(`${fileUrl}#page=1&toolbar=0&navpanes=0&scrollbar=0&view=FitV&zoom=75&pagemode=none`);
    } else if (ext === 'doc' || ext === 'docx') {
      setPreviewType('doc');
      // Use Microsoft Office Online Viewer to preview DOC/DOCX files
      const encodedUrl = encodeURIComponent(fileUrl);
      setPreviewUrl(`https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`);
    } else {
      setPreviewType('file');
      setPreviewUrl(null);
    }
    
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [fileUrl]);

  const renderPreview = () => {
    if (isLoading) {
      return (
        <div className="myresume-file-preview-loading">
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <span className="loading-text" style={{ fontSize: '10px' }}>Loading file...</span>
        </div>
      );
    }

    if (!fileUrl) {
      return (
        <div className="myresume-file-preview-loading">
          <div className="myresume-file-preview-spinner"></div>
          <span className="myresume-file-preview-loading-text">Loading...</span>
        </div>
      );
    }

    const ext = getFileExtension(fileUrl);
    
    switch (previewType) {
      case 'image':
        return (
          <div className="myresume-image-preview-container">
            {!imageLoaded && (
              <div className="myresume-image-loading-text">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
                <span style={{ fontSize: '10px' }}>Loading image...</span>
              </div>
            )}
            <img
              src={previewUrl}
              alt="File preview"
              className={`myresume-image-preview ${!imageLoaded ? 'myresume-image-preview-hidden' : ''}`}
              onLoad={() => {
                setImageLoaded(true);
              }}
              onError={(e) => {
                setPreviewType('file');
              }}
            />
          </div>
        );
      
      case 'pdf':
        return (
          <div className="myresume-pdf-preview-container">
            <div className="myresume-pdf-iframe-container">
              <iframe
                src={previewUrl}
                className="myresume-pdf-iframe"
                scrolling="no"
                onLoad={() => setIsLoading(false)}
                onError={() => setPreviewType('file')}
              />
            </div>
          </div>
        );
      
      case 'doc':
        return (
          <div className="myresume-pdf-preview-container">
            <div className="myresume-pdf-iframe-container">
              <iframe
                src={previewUrl}
                className="myresume-pdf-iframe"
                scrolling="no"
                onLoad={() => setIsLoading(false)}
                onError={() => setPreviewType('file')}
                title="Document preview"
              />
            </div>
          </div>
        );
      
      default:
        return (
          <div className="myresume-file-icon-container">
            <i className={`bx ${getFileIcon(ext)} myresume-file-icon myresume-file-color-${ext}`}></i>
            <span className="myresume-file-extension">
              {ext}
            </span>
          </div>
        );
    }
  };

  const getFileName = (url) => {
    if (!url) return '';
    return url.split('/').pop().split('?')[0].split('#')[0];
  };

  const displayFileName = fileName || getFileName(fileUrl);

  return (
    <div className="myresume-file-preview-item">
      <div 
        className="myresume-file-preview" 
        onClick={handleClick}
      >
        {renderPreview()}
      </div>
      {onRemove && (
        <div className="myresume-file-name-container">
          <span className="myresume-file-name" title={displayFileName}>
            {displayFileName}
          </span>
          <i 
            className="bx bx-trash myresume-trash-icon"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            title="Remove file"
          ></i>
        </div>
      )}
    </div>
  );
};

export default FilePreview;

